import type {
  AccountStatus,
  AdminUserDetail,
  AdminUserListPage,
  CompanyOutcome,
  CompanyOutcomeKind,
  CorrectCompanyClaimBody,
  ReassignCompanyBody,
} from '@nestjs-boilerplate/types';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiClient } from '@/lib/api-client';
import { manyOf, manyStrings, oneOf, toCsv } from '@/lib/query-params';
import { companyCreditKeys } from './company-credit';

export type {
  AdminUserDetail,
  AdminUserListItem,
  AdminUserListPage,
} from '@nestjs-boilerplate/types';

/** Page size the list requests when the caller does not say otherwise. */
const DEFAULT_PAGE_SIZE = 20;

/**
 * Filters and paging the user list can apply.
 *
 * A field left undefined is not sent, so the server sees no filter rather than
 * an empty one.
 */
export interface AdminUserFilters {
  search?: string;
  /** Any of these roles matches. Empty or absent means no role filter. */
  role?: string[];
  /** Any of these states matches. Empty or absent means no state filter. */
  state?: (
    | 'active'
    | 'pending_review'
    | 'unverified'
    | 'setup_incomplete'
    | 'rejected'
    | 'disabled'
    | 'deleted'
  )[];
  /** Joined on or after this date, as `YYYY-MM-DD`. */
  from?: string;
  /** Joined on or before this date, as `YYYY-MM-DD`. Inclusive of the whole day. */
  to?: string;
  page?: number;
  pageSize?: number;
  sortBy?: 'email' | 'name' | 'company' | 'role' | 'createdAt' | 'balance';
  sortOrder?: 'asc' | 'desc';
}

/** The format `<input type="date">` produces, and all the API needs to accept here. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** The `state` values the API accepts. Anything else is not a filter it knows. */
const USER_STATES: readonly NonNullable<AdminUserFilters['state']>[number][] = [
  'active',
  'pending_review',
  'unverified',
  'setup_incomplete',
  'rejected',
  'disabled',
  'deleted',
];

/** The columns the API can sort by. */
const USER_SORT_COLUMNS: readonly NonNullable<AdminUserFilters['sortBy']>[] = [
  'email',
  'name',
  'company',
  'role',
  'createdAt',
  'balance',
];

/** The sort directions the API accepts. */
const SORT_ORDERS: readonly NonNullable<AdminUserFilters['sortOrder']>[] = ['asc', 'desc'];

/** Narrows a value to a positive integer, or to undefined. */
function positiveInt(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;
}

/** Narrows a value to a non-empty string, or to undefined. */
function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Narrows a value to a `YYYY-MM-DD` date, or to undefined.
 *
 * Checked rather than forwarded for the same reason as every other parameter
 * here: a stray `?from=yesterday` in the URL would come back a 400 the reader
 * has no control to clear.
 */
function isoDate(value: unknown): string | undefined {
  return typeof value === 'string' && ISO_DATE_PATTERN.test(value) ? value : undefined;
}

export const adminUserQueryKeys = {
  all: ['admin', 'users'] as const,
  list: (filters: AdminUserFilters) => [...adminUserQueryKeys.all, 'list', filters] as const,
  detail: (id: string) => [...adminUserQueryKeys.all, 'detail', id] as const,
};

/**
 * Fetches one page of users.
 *
 * Every parameter is read by name and checked against what the API accepts,
 * rather than the filter object being forwarded wholesale. TanStack Router
 * merges a parent route's search params into a child's, and `/admin` declares
 * no `validateSearch`, so this object can carry keys this screen never defined
 * — `/admin/users?zzz=1` used to reach the API and come back a 400 the reader
 * had no way out of. A parameter that fails to narrow is simply not sent, which
 * degrades to the unfiltered list instead of to an error card.
 *
 * @param filters - Active filters, paging and ordering
 */
export async function getAdminUsers(filters: AdminUserFilters): Promise<AdminUserListPage> {
  const params = new URLSearchParams({
    page: String(positiveInt(filters.page) ?? 1),
    pageSize: String(positiveInt(filters.pageSize) ?? DEFAULT_PAGE_SIZE),
  });

  // Empty strings come from cleared inputs and must not become filters —
  // `search=` would otherwise be sent and rejected as too short.
  const search = nonEmptyString(filters.search);
  if (search) params.set('search', search);

  // Comma-separated rather than repeated: one parameter per filter keeps the
  // URL readable, and the API reads both encodings.
  const role = toCsv(manyStrings(filters.role));
  if (role) params.set('role', role);

  const state = toCsv(manyOf(USER_STATES, filters.state));
  if (state) params.set('state', state);

  const from = isoDate(filters.from);
  if (from) params.set('from', from);

  const to = isoDate(filters.to);
  if (to) params.set('to', to);

  const sortBy = oneOf(USER_SORT_COLUMNS, filters.sortBy);
  if (sortBy) params.set('sortBy', sortBy);

  const sortOrder = oneOf(SORT_ORDERS, filters.sortOrder);
  if (sortOrder) params.set('sortOrder', sortOrder);

  return apiClient.fetch<AdminUserListPage>(`/admin/users?${params.toString()}`);
}

/**
 * Fetches one user's full due-diligence record.
 *
 * @param id - The user to read
 */
export async function getAdminUser(id: string): Promise<AdminUserDetail> {
  return apiClient.fetch<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}`);
}

/**
 * Paged query over platform users.
 *
 * Keeps the previous page visible while the next one loads, so paging and
 * sorting do not blank the table out from under the reader.
 */
export function useAdminUsers(filters: AdminUserFilters) {
  return useQuery({
    queryKey: adminUserQueryKeys.list(filters),
    queryFn: () => getAdminUsers(filters),
    placeholderData: keepPreviousData,
  });
}

/**
 * One user's full record.
 */
export function useAdminUser(id: string) {
  return useQuery({
    queryKey: adminUserQueryKeys.detail(id),
    queryFn: () => getAdminUser(id),
  });
}

/** What every account-status transition returns. */
export interface UserStatusChangeResult {
  id: string;
  accountStatus: AccountStatus;
}

/** Body for a transition that requires the administrator to say why. */
export interface UserStatusReasonBody {
  id: string;
  reason: string;
}

/**
 * What approving this applicant would do with the company they claimed.
 *
 * @param id - The applicant
 */
export async function fetchCompanyOutcome(id: string): Promise<CompanyOutcome> {
  return apiClient.fetch<CompanyOutcome>(`/admin/users/${encodeURIComponent(id)}/company-outcome`);
}

/**
 * Corrects the company details a pending applicant claimed.
 *
 * @param body - The applicant, and only the fields being corrected
 */
export async function correctCompanyClaim({
  id,
  ...patch
}: { id: string } & CorrectCompanyClaimBody): Promise<{ id: string }> {
  return apiClient.fetch<{ id: string }>(`/admin/users/${encodeURIComponent(id)}/company-claim`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * Moves an approved user to a different company.
 *
 * @param body - The user, the destination firm, the firm they were shown in,
 * and why
 */
export async function reassignCompany({
  id,
  ...patch
}: { id: string } & ReassignCompanyBody): Promise<{ id: string }> {
  return apiClient.fetch<{ id: string }>(`/admin/users/${encodeURIComponent(id)}/company`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * Grants a reviewed applicant access and assigns them a company.
 *
 * `expectedOutcome` is what the confirmation modal showed. The server refuses
 * if it has changed since, so a company created in another tab cannot turn a
 * "create" into an attachment to a firm the administrator never saw.
 */
export async function approveUser({
  id,
  ...expected
}: {
  id: string;
  expectedOutcome?: CompanyOutcomeKind;
  /** The firm the modal named, so an attach cannot land on a different one. */
  expectedCompanyId?: string;
}): Promise<UserStatusChangeResult> {
  return apiClient.fetch<UserStatusChangeResult>(`/admin/users/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    body: JSON.stringify(expected),
  });
}

/**
 * Turns a reviewed applicant away.
 *
 * @param body - The applicant and why they were turned away
 */
export async function rejectUser({
  id,
  reason,
}: UserStatusReasonBody): Promise<UserStatusChangeResult> {
  return apiClient.fetch<UserStatusChangeResult>(`/admin/users/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/**
 * Suspends an approved account.
 *
 * @param body - The account and why it was suspended
 */
export async function disableUser({
  id,
  reason,
}: UserStatusReasonBody): Promise<UserStatusChangeResult> {
  return apiClient.fetch<UserStatusChangeResult>(`/admin/users/${encodeURIComponent(id)}/disable`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/**
 * Restores a disabled account.
 *
 * @param id - The account
 */
export async function enableUser(id: string): Promise<UserStatusChangeResult> {
  return apiClient.fetch<UserStatusChangeResult>(`/admin/users/${encodeURIComponent(id)}/enable`, {
    method: 'POST',
  });
}

/**
 * Shared plumbing behind the four status-transition hooks.
 *
 * Every transition invalidates both the detail the screen is showing and the
 * list behind it, so an admin cannot approve — or disable — the same person
 * twice because the list they went back to still shows the old status.
 */
function useUserStatusMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<UserStatusChangeResult>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: adminUserQueryKeys.detail(result.id) });
      queryClient.invalidateQueries({ queryKey: adminUserQueryKeys.all });
    },
    onError: (error: Error) => {
      // 404 (unknown user), 409 (illegal transition) and 403 (self-disable)
      // all arrive here as ordinary Errors from apiClient — surfaced rather
      // than swallowed, since a silent failure reads as "nothing happened".
      toast.error(error.message || 'Failed to update the account status');
    },
  });
}

/** Approves a pending applicant. Takes `{ id, expectedOutcome }`. */
export function useApproveUser() {
  return useUserStatusMutation(approveUser);
}

/**
 * Corrects a pending applicant's claimed company details.
 *
 * Its own mutation rather than `useUserStatusMutation`: that helper is typed to
 * a status change, and this returns none — a correction is not a transition.
 * The cache invalidation matches, though, and for the same reason plus one: a
 * correction changes the detail page, and can change what the approval modal
 * resolves to.
 */
export function useCorrectCompanyClaim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: correctCompanyClaim,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminUserQueryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: adminUserQueryKeys.all });
    },
    onError: (error: Error) => {
      // 400 (a number the applicant could not have submitted), 404 (no claim)
      // and 409 (already decided) all arrive as ordinary Errors from apiClient.
      toast.error(error.message || 'Failed to correct the company claim');
    },
  });
}

/**
 * Moves an approved user to another firm.
 *
 * Invalidates the whole admin user space, not just this record: the company
 * column on the list changes too, and so does the destination firm's user
 * count on the companies screen.
 */
export function useReassignCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reassignCompany,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: adminUserQueryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: adminUserQueryKeys.all });
      // The move changes the user count on BOTH firms, and that list is cached
      // for five minutes by the global staleTime. Without this, reopening the
      // dialog or visiting the companies screen inside that window shows the
      // old counts — and the Delete action keys off `userCount === 0`, so a
      // stale zero would offer deletion of a firm that just gained a member,
      // while a stale one would hide it for the firm that just lost its last.
      queryClient.invalidateQueries({ queryKey: companyCreditKeys.all });
    },
    onError: (error: Error) => {
      // 404 (unknown firm), and 409 for "not approved", "already there" and
      // "moved since you looked" — each carries a message worth showing rather
      // than flattening into one.
      toast.error(error.message || 'Failed to move the user');
    },
  });
}

/**
 * The company decision awaiting an administrator, for the confirmation modal.
 *
 * Only enabled while the modal is open — resolving it on every detail-page
 * render would query for a decision nobody is making.
 */
export function useCompanyOutcome(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...adminUserQueryKeys.detail(id), 'company-outcome'],
    queryFn: () => fetchCompanyOutcome(id),
    enabled,
    // Always refetched when the modal opens: a stale answer here is one an
    // administrator could confirm without it being true any more.
    staleTime: 0,
  });
}

/** Rejects a pending applicant. Takes `{ id, reason }`. */
export function useRejectUser() {
  return useUserStatusMutation(rejectUser);
}

/** Disables an approved account. Takes `{ id, reason }`. */
export function useDisableUser() {
  return useUserStatusMutation(disableUser);
}

/** Re-enables a disabled account. Takes the bare user id. */
export function useEnableUser() {
  return useUserStatusMutation(enableUser);
}
